import { useState, useEffect } from "react";
import { API } from "@/lib/constants";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Shield, Loader2 } from "lucide-react";

// ── User Roles & Functions Popover ─────────────────────────────────────
// Consolidates four columns (Sales, Amplify Approver, Analyst, Designer)
// into a single "Edit roles" dialog. Admins are implicit-everything: the
// dialog renders a read-only summary for them.
//
// "Sales" semantics (May 2026): a user with Sales toggled on is eligible
// to be assigned ownership of clients / opportunities. The underlying
// storage is still ``user.sales_role`` — set to ``"Sales"`` when the
// toggle is on, empty string when off. ``execution_board.py`` already
// filters on ``sales_role != ""`` for sales-rep pickers.

const SALES_FLAG_VALUE = "Sales";

function isSalesEnabled(user) {
  // Any non-empty sales_role counts as Sales-enabled — preserves legacy
  // free-text values (e.g. "Sales Rep", "Sales Lead") set before the
  // toggle refactor.
  return !!(user?.sales_role && String(user.sales_role).trim());
}

export default function UserRolesPopover({ open, onClose, user, isSelf, onSaved }) {
  const isAdmin = user?.role === "admin";
  const [isSales, setIsSales] = useState(isSalesEnabled(user));
  const [canApprove, setCanApprove] = useState(!!user?.can_approve_amplify);
  const [isAnalyst, setIsAnalyst] = useState(!!user?.is_analyst);
  const [isDesigner, setIsDesigner] = useState(!!user?.is_designer);
  const [saving, setSaving] = useState(false);

  // Re-seed local state every time the dialog opens for a (possibly different) user.
  useEffect(() => {
    if (open && user) {
      setIsSales(isSalesEnabled(user));
      setCanApprove(!!user.can_approve_amplify);
      setIsAnalyst(!!user.is_analyst);
      setIsDesigner(!!user.is_designer);
    }
  }, [open, user]);

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    const errors = [];

    // 1. Sales toggle → stored as user.sales_role string ("Sales" | "").
    if (isSalesEnabled(user) !== isSales) {
      try {
        const r = await fetch(`${API}/admin/users/${user.user_id}/sales-role`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ sales_role: isSales ? SALES_FLAG_VALUE : "" }),
        });
        if (!r.ok) errors.push("sales");
      } catch { errors.push("sales"); }
    }

    // The remaining three toggles only apply to non-admins (admins are
    // implicit-everything in the source-of-truth filters) and you cannot
    // toggle yourself out of a function.
    if (!isAdmin && !isSelf) {
      if (!!user.can_approve_amplify !== canApprove) {
        try {
          const r = await fetch(`${API}/admin/users/${user.user_id}/can-approve-amplify`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ can_approve_amplify: canApprove }),
          });
          if (!r.ok) errors.push("amplify approver");
        } catch { errors.push("amplify approver"); }
      }
      if (!!user.is_analyst !== isAnalyst) {
        try {
          const r = await fetch(`${API}/admin/users/${user.user_id}/operational-role`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ role: "analyst", value: isAnalyst }),
          });
          if (!r.ok) errors.push("analyst");
        } catch { errors.push("analyst"); }
      }
      if (!!user.is_designer !== isDesigner) {
        try {
          const r = await fetch(`${API}/admin/users/${user.user_id}/operational-role`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ role: "designer", value: isDesigner }),
          });
          if (!r.ok) errors.push("designer");
        } catch { errors.push("designer"); }
      }
    }

    setSaving(false);
    if (errors.length === 0) {
      toast.success("Roles updated");
      onSaved?.();
      onClose?.();
    } else {
      toast.error(`Failed to update: ${errors.join(", ")}`);
      onSaved?.();
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="sm:max-w-md" data-testid="user-roles-popover">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Roles &amp; Functions
            {isAdmin && (
              <Badge className="bg-red-100 text-red-700 text-[10px]">
                <Shield className="w-3 h-3 mr-1" /> Admin
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            {user.name} · <span className="text-slate-500">{user.email}</span>
          </DialogDescription>
        </DialogHeader>

        {isAdmin ? (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
            <p className="font-medium">All functions enabled automatically.</p>
            <p className="mt-1 text-xs text-emerald-800">
              Admins are implicit Sales Lead, Amplify Approver, Analyst and Designer.
              Change role to <span className="font-medium">User</span> in the table first to toggle individual functions.
            </p>
          </div>
        ) : (
          <div className="space-y-3 py-2">
            <ToggleRow
              testid="popover-toggle-sales"
              label="Sales"
              hint="Eligible to be assigned ownership of clients & opportunities."
              checked={isSales}
              onChange={setIsSales}
              disabled={isSelf}
            />
            <ToggleRow
              testid="popover-toggle-analyst"
              label="Analyst"
              hint="Eligible to be assigned (or self-claim) Lens triage cards."
              checked={isAnalyst}
              onChange={setIsAnalyst}
              disabled={isSelf}
            />
            <ToggleRow
              testid="popover-toggle-designer"
              label="Designer"
              hint="Eligible to pick up Amplify Kanban design cards."
              checked={isDesigner}
              onChange={setIsDesigner}
              disabled={isSelf}
            />
            <ToggleRow
              testid="popover-toggle-approver"
              label="Amplify Approver"
              hint="Can approve / reject Amplify content cards."
              checked={canApprove}
              onChange={setCanApprove}
              disabled={isSelf}
            />

            {isSelf && (
              <p className="text-xs text-slate-500 italic pt-2">
                You can&apos;t change your own functions. Ask another admin.
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="popover-cancel">
            Cancel
          </Button>
          {!isAdmin && (
            <Button onClick={handleSave} disabled={saving || isSelf} data-testid="popover-save">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ToggleRow({ label, hint, checked, onChange, disabled, testid }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-slate-800">{label}</p>
        <p className="text-xs text-slate-500">{hint}</p>
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onChange}
        disabled={disabled}
        data-testid={testid}
      />
    </div>
  );
}

// Helper: derive a short chip summary for the table view.
export function summariseUserRoles(u) {
  if (u.role === "admin") return [{ label: "All functions", tone: "emerald" }];
  const chips = [];
  if (u.sales_role && String(u.sales_role).trim()) chips.push({ label: "Sales", tone: "blue" });
  if (u.is_analyst) chips.push({ label: "Analyst", tone: "indigo" });
  if (u.is_designer) chips.push({ label: "Designer", tone: "violet" });
  if (u.can_approve_amplify) chips.push({ label: "Approver", tone: "emerald" });
  return chips;
}
